import { Link, useLocation } from '@solidjs/router';
import { createEffect, createSignal, Match, onMount, Show, Switch, useContext, onCleanup } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../../tools/error-catcher';
import { LockedContent } from '../../../ui-components/layouts/locked-content';
import { getImageServiceSrcPath } from '../../image-component/image-component';
import { getVimeoUrl, getYoutubeVideoId } from '../../video-embed/helpers';
import { VideoPostCardProps } from './video-post-card-types';
import { MyContentHandler } from '../../my-content-handler/my-content-handler';
import { removeTrailingSlash } from '../../../tools/remove-trailing-slash';
import removeAmpersand from '../../../tools/remove-ampersand';
import { VideoImageBeforeCookiesAccepted } from '../../video-embed/video-image-before-cookie';
import { CookieModal } from '../../../ui-components/cookie-modal/cookie-modal';
import { TabHandler } from '../../../tools/tab-handler';
import { Badge } from '../../../ui-components/badge/badge';
import { HeartCviIcon } from '../../icons-library/heart-cvi';
import { HeartCviFilledIcon } from '../../icons-library/heart-cvi-filled';
import { addMyContent, deleteMyContentItemByPermalink, getMyContent, MyContentItem } from '../../my-content/my-content-helpers';

declare global {
    interface Window {
        Cookiebot: any;
    }
}

const SRCSET_WIDTHS = [480, 600, 750, 960, 1200, 1500];

export const VideoPostCard = (props: VideoPostCardProps) => {
    const {
        imagesServiceUrl,
        supportedImageFormats,
        userState,
        pageRegistration,
        videosEntityName,
        localize,
        siteInfo,
        userProfileUrls,
    } = useContext(AppContext);
    const location = useLocation();

    const [showModal, setShowModal] = createSignal(false);
    const [playRequested, setPlayRequested] = createSignal(false);
    const [videoCookiesAccepted, setVideoCookiesAccepted] = createSignal(false);

    const [isSaved, setIsSaved] = createSignal(false);

    /* Same bookmark heart as the event/editorial card: over the media, bottom-right, on the
       secondary-neutral icon button. Hidden on the bookmarks page, where the card renders the
       standalone remove heart instead. */
    const showSaveHeart = () =>
        !props.hideMyContentStar
        && siteInfo.siteType === 'default'
        && location.pathname !== userProfileUrls.bookmarks;

    createEffect(() => {
        const permalink = props.permalink;
        if (!permalink) {
            setIsSaved(false);
            return;
        }
        const existing = (getMyContent() as MyContentItem[]).find((item) => item.permalink === permalink);
        setIsSaved(Boolean(existing));
    });

    const toggleSaved = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const permalink = props.permalink || '/';
        if (isSaved()) {
            deleteMyContentItemByPermalink(permalink);
            setIsSaved(false);
            return;
        }
        addMyContent({ title: props.title, permalink, type: 'video' });
        setIsSaved(true);
    };

    const [firstTabbableElement, setFirstTabbableElement] = createSignal<HTMLElement | undefined>();
    const [lastTabbableElement, setLastTabbableElement] = createSignal<HTMLElement | undefined>();

    const checkConsent = () => {
        if (window?.Cookiebot?.consent?.marketing) {
            setVideoCookiesAccepted(true);
        }
    };

    onMount(() => {
        checkConsent();
        window.addEventListener('CookiebotOnAccept', checkConsent);
        onCleanup(() => window.removeEventListener('CookiebotOnAccept', checkConsent));
    });

    /* Facade: every card shows the same thumbnail + play button. The player only mounts on a
       playable card once the user has clicked it and marketing cookies are accepted, so the
       listing and the single-video page look identical until playback starts. */
    const showPlayer = () => Boolean(props.ableToPlay) && playRequested() && videoCookiesAccepted();

    const handleThumbnailClick = (e: MouseEvent) => {
        if (!props?.ableToPlay) return;
        e.preventDefault();
        setPlayRequested(true);
        if (!videoCookiesAccepted()) {
            setShowModal(true);
        }
    };

    if (!props.videoUrl) return null;

    const isHiddenForPublic = () => {
        return props.isHealthcareProfessional 
            && !props.isVisibleForPublic 
            && !userState.user 
            && pageRegistration.url !== '';
    };

    const buildImageSrc = (width: number) => {
        if (!props?.image?.url) return '';
        return getImageServiceSrcPath({
            imagesServiceUrl,
            supportedImageFormats,
            src: props.image.url,
            width,
            height: 'AUTO',
        });
    };

    const srcset = () => SRCSET_WIDTHS.map((w) => `${buildImageSrc(w)} ${w}w`).join(', ');

    const postTypeSlug = '/video-item/';
    const permalinkWithEntityName = videosEntityName && props.permalink && props.permalink.replace(postTypeSlug, `/${videosEntityName}/`);
    const finalHref = permalinkWithEntityName ? removeTrailingSlash(permalinkWithEntityName) : '';

    /* Listing cards navigate rather than play, so a play button there is a false promise.
       A DS badge (Storybook `components-badge--highlight`) names the content type instead,
       placed like the course card's status badge. */
    const isListingCard = () => !props.ableToPlay;

    const MediaContent = () => (
        <div class={clsx(
            'relative w-full overflow-hidden rounded-cvi-md bg-cvi-bg-neutral-subtle',
            props.mediaOnly ? 'aspect-video border border-cvi-border-default' : 'aspect-[29/16]',
        )}>
            <Show when={isListingCard()}>
                <Badge variant="highlight" class="absolute right-cvi-6 top-cvi-6 z-[2]">
                    {localize('video', 'Video')}
                </Badge>
            </Show>

            {/* Above the full-card overlay link (z-10) so it keeps its own click. Off while the
                player is mounted — it would sit on top of the iframe. */}
            <Show when={showSaveHeart() && !showPlayer()}>
                <button
                    type="button"
                    class="icon-button-secondary-neutral icon-button-secondary-neutral-sm absolute right-cvi-6 bottom-cvi-6 z-20"
                    aria-label={
                        isSaved() ? localize('my_content_remove_star', 'Remove from My Content') : localize('save-to-my-content', 'Save to my content')
                    }
                    aria-pressed={isSaved()}
                    onClick={toggleSaved}
                >
                    <Show when={isSaved()} fallback={<HeartCviIcon size="small" />}>
                        <HeartCviFilledIcon size="small" />
                    </Show>
                </button>
            </Show>
            <Switch>
                <Match when={showPlayer() && props?.videoUrl?.includes('youtu')}>
                    <iframe
                        class="absolute inset-0 size-full border-0"
                        src={`https://www.youtube-nocookie.com/embed/${getYoutubeVideoId(props.videoUrl)}?autoplay=1&mute=1`}
                        title="YouTube video player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                        referrerpolicy="strict-origin-when-cross-origin"
                    ></iframe>
                </Match>
                <Match when={showPlayer() && props?.videoUrl?.includes('vimeo')}>
                    <iframe
                        class="absolute inset-0 size-full border-0"
                        src={getVimeoUrl(props.videoUrl, true)}
                        title="Vimeo video player"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowfullscreen
                        referrerpolicy="strict-origin-when-cross-origin"
                    ></iframe>
                </Match>
                <Match when={true}>
                    <VideoImageBeforeCookiesAccepted
                        videoUrl={props.videoUrl}
                        image={props?.image}
                        bgImage={buildImageSrc(750)}
                        srcset={srcset()}
                        sizes="(min-width: 1270px) 25vw, (min-width: 768px) 33vw, 100vw"
                        hidePlayButton={isHiddenForPublic() || isListingCard()}
                    />
                </Match>
            </Switch>
        </div>
    );

    const cookieModal = () => (
        <Show when={showModal()}>
            <TabHandler refs={{ firstTabbableElement, lastTabbableElement }}>
                <CookieModal
                    close={() => setShowModal(false)}
                    setFirstTabbableElement={setFirstTabbableElement}
                    setLastTabbableElement={setLastTabbableElement}
                />
            </TabHandler>
        </Show>
    );

    if (props.mediaOnly) {
        return (
            <ErrorCatcher componentName="VideoPostCard">
                <div class="group relative w-full">
                    <MediaContent />
                    <Show when={!showPlayer()}>
                        <button
                            type="button"
                            onClick={handleThumbnailClick}
                            aria-label={removeAmpersand(props.title)}
                            class="absolute inset-0 z-10 cursor-pointer rounded-cvi-md border-0 bg-transparent"
                        >
                            <span class="sr-only">{removeAmpersand(props.title)}</span>
                        </button>
                    </Show>
                    {cookieModal()}
                </div>
            </ErrorCatcher>
        );
    }

    return (
        <ErrorCatcher componentName="VideoPostCard">
            {/* h-full on both wrappers so cards in a grid row stretch to the tallest one. */}
            <div class="relative w-full h-full">
                {/* Same surface as the event/editorial card: no border, no hover shadow — the
                    rounded media box is the only framed element and the text sits flush below. */}
                <div class="group relative flex h-full flex-col gap-cvi-6">
                    <MediaContent />

                    <div class="flex flex-1 flex-col gap-cvi-4">
                        <h3 class={clsx(
                            'heading-lg desktop:text-heading-md desktop:leading-heading-md font-cvi-medium text-cvi-fg-default m-0',
                            props.insideSlider && 'line-clamp-2'
                        )}>
                            {removeAmpersand(props.title)}
                        </h3>

                        <Show when={isHiddenForPublic()}>
                            <div class="mt-cvi-2">
                                <LockedContent isColumn={false} />
                            </div>
                        </Show>

                    </div>

                    {/* Full-card overlay: navigates on listing cards, starts playback on playable
                        ones. Inside the card so it shares its stacking context with the heart. */}
                    <Show when={!showPlayer()}>
                        <Link
                            href={finalHref}
                            onClick={handleThumbnailClick}
                            aria-label={removeAmpersand(props.title)}
                            class="absolute inset-0 z-10 rounded-cvi-md no-underline"
                        >
                            <span class="sr-only">{removeAmpersand(props.title)}</span>
                        </Link>
                    </Show>
                </div>

                <MyContentHandler action="remove" permalink={props.permalink} />

                {cookieModal()}
            </div>
        </ErrorCatcher>
    );
};